/**
 * Trauma-based screenshake (TRD-04 §4).
 *
 * Two decisions here are the entire difference between good shake and bad shake:
 *
 * 1. **Trauma is squared before use.** A linear response makes every small hit shove the
 *    screen; squaring keeps ordinary hits subtle and saves the violence for boss deaths.
 *
 * 2. **Offsets come from NOISE, not from `random()` per frame.** Per-frame random
 *    produces a high-frequency buzz that reads as a rendering fault. Sampling a smooth
 *    noise curve over time produces an actual shake — the camera travels somewhere
 *    between frames instead of teleporting.
 *
 * The noise table is precomputed once with `Math.random`, which is fine: this is
 * presentation and never touches the simulation (R-04-01).
 */

const NOISE_SIZE = 256;

/** Three independent smooth channels: x offset, y offset, rotation. */
function buildNoiseTable(): Float32Array[] {
  const channels: Float32Array[] = [];
  for (let c = 0; c < 3; c++) {
    const table = new Float32Array(NOISE_SIZE);
    for (let i = 0; i < NOISE_SIZE; i++) table[i] = Math.random() * 2 - 1;
    channels.push(table);
  }
  return channels;
}

const NOISE = buildNoiseTable();

/**
 * Smoothly interpolated value noise in [-1, 1].
 *
 * `t` indexes the table DIRECTLY — one unit of t is one table entry. An earlier version
 * scaled t by the table size, which advanced ~12 entries per frame and sampled what were
 * effectively independent random values: the exact per-frame buzz this function exists to
 * avoid. Caught by the smoothness test, not by looking at it.
 */
function sampleNoise(channel: number, t: number): number {
  const table = NOISE[channel] ?? NOISE[0];
  if (!table) return 0;
  const i = Math.floor(t);
  const frac = t - i;
  const a = table[((i % NOISE_SIZE) + NOISE_SIZE) % NOISE_SIZE] ?? 0;
  const b = table[(((i + 1) % NOISE_SIZE) + NOISE_SIZE) % NOISE_SIZE] ?? 0;
  // Smoothstep the blend so the curve has no corners, or the shake gains a visible tick.
  const s = frac * frac * (3 - 2 * frac);
  return a + (b - a) * s;
}

export const MAX_OFFSET = 6;
export const MAX_ANGLE = 0.03;
const DECAY_PER_SECOND = 1.8;
/** Noise-table entries traversed per second — roughly the shake's oscillation rate. */
const FREQUENCY = 22;

export interface ShakeOffset {
  x: number;
  y: number;
  rotation: number;
}

export class Screenshake {
  private trauma = 0;
  private elapsed = 0;

  /**
   * Accessibility requirement, not a nicety (R-04-11). Some players genuinely cannot
   * play with camera shake, and 0 must be a fully supported value rather than a
   * degraded one.
   */
  scale = 1;

  readonly offset: ShakeOffset = { x: 0, y: 0, rotation: 0 };

  add(amount: number): void {
    this.trauma = Math.min(1, this.trauma + amount);
  }

  get current(): number {
    return this.trauma;
  }

  update(deltaMs: number): ShakeOffset {
    this.elapsed += deltaMs;

    if (this.trauma <= 0 || this.scale <= 0) {
      this.offset.x = 0;
      this.offset.y = 0;
      this.offset.rotation = 0;
      return this.offset;
    }

    const shake = this.trauma * this.trauma;
    const t = (this.elapsed / 1000) * FREQUENCY;

    this.offset.x = shake * MAX_OFFSET * this.scale * sampleNoise(0, t);
    this.offset.y = shake * MAX_OFFSET * this.scale * sampleNoise(1, t);
    this.offset.rotation = shake * MAX_ANGLE * this.scale * sampleNoise(2, t);

    this.trauma = Math.max(0, this.trauma - (DECAY_PER_SECOND * deltaMs) / 1000);
    return this.offset;
  }

  reset(): void {
    this.trauma = 0;
    this.offset.x = 0;
    this.offset.y = 0;
    this.offset.rotation = 0;
  }
}

/**
 * Trauma per event (R-04-10).
 *
 * Note that firing contributes ZERO. Forty towers firing would otherwise hold the screen
 * in permanent motion, which turns shake from punctuation into noise and makes the real
 * impacts unreadable.
 */
export const TRAUMA = {
  hit: 0.02,
  crit: 0.1,
  /** Between a hit and a crit: frequent enough that it must not dominate, big enough
   *  that a detonation is felt and not just seen. */
  explosion: 0.06,
  kill: 0.05,
  bossKill: 0.6,
  coreDamaged: 0.35,
  synergyTier6: 0.25,
  waveCleared: 0.15,
} as const;
